import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { buildReferralsView, type ReferralsView } from './referralsModel';
import type { Transaction, User } from '../dataProvider/types';

/**
 * Region-driven Referrals view, read through the aggregation seam
 * (`buildReferralsView`), NOT computed in the component. For v1 it pulls the raw
 * users and transactions from the mock provider and aggregates client-side; the
 * real-data migration replaces the fetch-and-aggregate here with a single
 * server-side endpoint and the surface does not change. Same seam idiom as
 * useSubscriptions.
 *
 * Users carry the referral self-FK and reward state; transactions give the
 * referee's qualifying spend for the threshold-progress display. The aggregator
 * reads only these two collections.
 *
 * Scope comes from the region context (UK | US | GLOBAL): the surface shows every
 * band the scope derives to at once, so it keys off `region`, not `activeRegion`.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useReferrals = (): {
  view: ReferralsView | null;
  isLoading: boolean;
} => {
  const dataProvider = useDataProvider();
  const { region } = useRegion();

  const { data, isLoading } = useQuery({
    queryKey: ['referrals', region],
    queryFn: async () => {
      const [users, transactions] = await Promise.all([
        dataProvider.getList('users', ALL),
        dataProvider.getList('transactions', ALL),
      ]);
      return buildReferralsView(
        {
          users: users.data as unknown as User[],
          transactions: transactions.data as unknown as Transaction[],
        },
        { scope: region },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
